import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Skeleton } from "antd";
import { cn } from "@/shared/lib/utils";
import { useUploadHistory } from "@/portals/patient/context/UploadHistoryContext";
import { InfiniteScrollContainer } from "@/shared/components/InfiniteScrollContainer";

// Row height (~70px: 16px padding top+bottom + a ~38px two-line text block)
// plus 12px gaps between rows, rounded up per breakpoint — 3 rows fit before
// scrolling on a small/laptop screen, more on a bigger desktop viewport,
// rather than a "3, always" cap regardless of how much room is actually
// available. Static string literal (not interpolated) so Tailwind's
// build-time scanner generates these classes.
const SCROLL_HEIGHT_CLASSES = "max-h-[236px] lg:max-h-[400px] xl:max-h-[560px] 2xl:max-h-[720px]";

function getFileIcon(fileName: string): string {
  const ext = fileName.split(".").pop()?.toLowerCase() || "";
  if (ext === "zip") return "lucide:archive";
  if (ext === "json") return "lucide:file-json";
  if (ext === "xml") return "lucide:file-code";
  if (ext === "csv") return "lucide:file-text";
  if (ext === "sql") return "lucide:database";
  if (ext === "tif" || ext === "tiff") return "lucide:file-image";
  if (["png", "jpg", "jpeg", "bmp"].includes(ext)) return "lucide:image";
  return "lucide:file-text";
}

function formatUploadDate(timestampMs: number): string {
  return new Date(timestampMs).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** Every completed upload currently loaded, each a clickable card that opens
 *  straight into that upload's Document Panel entry — as many as fit the
 *  screen show in full, the rest scroll (see SCROLL_HEIGHT_CLASSES) rather
 *  than being cut off at a fixed count. One shared component — used on both
 *  the Upload Records page and the Dashboard (end of Health Timeline) — so
 *  the two never drift apart and always redirect the same way. Renders
 *  nothing once loaded if the patient genuinely has no uploads yet. */
export function RecentUploadsCard({ className, heading = "Recent Uploads" }: { className?: string; heading?: string }) {
  const navigate = useNavigate();
  const { zipUploads, isLoading } = useUploadHistory();
  const recentSuccessfulUploads = zipUploads.filter((z) => z.status === "complete");

  if (isLoading) {
    return (
      <div className={cn("rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm", className)}>
        <h3 className="text-sm font-extrabold text-[#0A2947]">{heading}</h3>
        <div className="mt-4 flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 rounded-2xl border border-[#E5E7EB] px-4 py-3">
              <Skeleton.Avatar active size={36} shape="circle" />
              <div className="min-w-0 flex-1">
                <Skeleton active title={{ width: "50%" }} paragraph={{ rows: 1, width: "30%" }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (recentSuccessfulUploads.length === 0) return null;

  return (
    <div className={cn("rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm", className)}>
      <h3 className="text-sm font-extrabold text-[#0A2947]">{heading}</h3>
      <InfiniteScrollContainer className={cn("mt-4", SCROLL_HEIGHT_CLASSES)}>
        {/* gap on a flex column, not space-y — immune to any margin-collapse
            from elsewhere in the cascade, which is what was causing these
            cards to visually crowd/overlap before. */}
        <div className="flex flex-col gap-3 pr-1">
          {recentSuccessfulUploads.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => navigate(`/patient/documents?uploadId=${entry.id}`)}
              className="flex w-full items-center gap-3 rounded-2xl border border-[#E5E7EB] bg-white px-4 py-3 text-left transition-colors hover:border-[#F36F8F]/40 hover:bg-[#FFF9FA]"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F7F7F7]">
                <Icon icon={getFileIcon(entry.fileName)} className="h-4 w-4 !text-[#0A2947]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-[#0A2947]">{entry.title || entry.fileName}</p>
                <p className="text-xs text-[#9CA3AF]">{formatUploadDate(entry.uploadedAt)}</p>
              </div>
              <Icon icon="lucide:chevron-right" className="h-4 w-4 shrink-0 !text-[#9CA3AF]" />
            </button>
          ))}
        </div>
      </InfiniteScrollContainer>
    </div>
  );
}
